/* ============================================================
   Eigenes JavaScript.
   Gegenstueck zu assets/custom.css: assets/theme.js gehoert dem gekauften
   Theme und bleibt unberuehrt (Architekturregel 1 in README.md).
   ============================================================ */

(() => {
  'use strict';

  /* ----------------------------------------------------------
     Anker-Scroll mit eigener Daempfungskurve.

     Das Theme scrollt Anker per scrollIntoView({behavior:'smooth'}).
     Wir wollen eine ruhigere, distanzabhaengige Bewegung.

     Zusammenspiel mit dem Theme: assets/theme.js haengt einen Delegate an
     document.documentElement (Bubble-Phase) und beginnt seinen Handler mit
     "if (event.defaultPrevented) return". Wir hoeren deshalb auf document in
     der CAPTURE-Phase, laufen also garantiert vorher, und rufen
     preventDefault(). Der Theme-Handler steigt daraufhin von selbst aus.
     Bewusst KEIN stopPropagation(): das wuerde die Bubble-Phase komplett
     abschneiden und andere Delegates des Themes mit erledigen.
     Weil der Theme-Handler aussteigt, muessen wir sein hashchange:simulate
     selbst senden — darauf reagieren z.B. die Tab-/Accordion-Sections.
     ---------------------------------------------------------- */

  const DECAY = 3.2; // je groesser, desto schneller laeuft die Bewegung aus
  const ARRIVAL_THRESHOLD_PX = 0.5;
  // War 2px: zu eng fuer Subpixel-/Rundungsjitter auf hochaufloesenden
  // iOS-Displays, echte Interrupts (Wheel, Touch, Sprungmarke) bewegen die
  // Position um ein Vielfaches davon.
  const INTERRUPT_TOLERANCE_PX = 4;

  let scrollToken = 0;

  function getAnchorScrollOffset(anchorElement) {
    const rootStyle = window.getComputedStyle(document.documentElement);
    const targetStyle = window.getComputedStyle(anchorElement);
    const scrollPaddingTop = parseFloat(rootStyle.scrollPaddingTop) || 0;
    const scrollMarginTop = parseFloat(targetStyle.scrollMarginTop) || 0;

    // Addieren, nicht max(): der Browser tut bei scrollIntoView dasselbe.
    // scroll-padding verkleinert den Scrollport oben, scroll-margin vergroessert
    // die Zielbox nach oben — beide wirken zusammen. Hier sind beide gesetzt
    // (theme.css: html{scroll-padding-top} und .shopify-section:has(.section-spacing)
    // {scroll-margin-block-start}), mit max() landete das Ziel also rund eine
    // Headerhoehe zu tief unter der Sticky-Leiste.
    return scrollPaddingTop + scrollMarginTop;
  }

  function smoothScrollToElement(anchorElement) {
    const token = ++scrollToken;
    const offset = getAnchorScrollOffset(anchorElement);
    const maxY = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
    const targetY = Math.min(
      maxY,
      Math.max(0, anchorElement.getBoundingClientRect().top + window.scrollY - offset)
    );

    let currentY = window.scrollY;
    let lastTimestamp = null;
    let expectedY = null;

    // Natives Smooth-Scrolling bricht ab, sobald der Nutzer selbst scrollt.
    // Ohne das hier wuerde die Animation ihn jeden Frame zurueckreissen.
    const cancel = () => {
      scrollToken++;
      removeInterruptListeners();
    };
    const interruptEvents = ['wheel', 'touchstart', 'keydown'];
    const removeInterruptListeners = () => {
      interruptEvents.forEach((name) => window.removeEventListener(name, cancel));
    };
    interruptEvents.forEach((name) => window.addEventListener(name, cancel, { passive: true }));

    function step(timestamp) {
      if (token !== scrollToken) {
        // Neuer Anker-Klick oder Nutzerabbruch. Die eigenen Listener muessen
        // hier mit weg, sonst bleibt pro abgeloester Animation einer auf
        // window haengen und erhoeht spaeter scrollToken fuer eine fremde.
        removeInterruptListeners();
        return;
      }

      // Zweite Abbruchsicherung: Scrollbalken, Trackpad-Impulse und
      // Sprungmarken loesen nicht zwingend eines der Events oben aus.
      if (expectedY !== null && Math.abs(window.scrollY - expectedY) > INTERRUPT_TOLERANCE_PX) {
        removeInterruptListeners();
        return;
      }

      if (lastTimestamp === null) {
        lastTimestamp = timestamp;
      }
      // Deckel gegen Riesenspruenge, wenn der Tab zwischendurch inaktiv war.
      const deltaSeconds = Math.min((timestamp - lastTimestamp) / 1000, 0.1);
      lastTimestamp = timestamp;

      const remaining = targetY - currentY;
      if (Math.abs(remaining) < ARRIVAL_THRESHOLD_PX) {
        window.scrollTo(0, targetY);
        removeInterruptListeners();
        return;
      }

      currentY += remaining * (1 - Math.exp(-deltaSeconds * DECAY));
      window.scrollTo(0, currentY);

      // expectedY ist der Wert, den wir selbst gesetzt haben, nicht das
      // sofortige Zuruecklesen von window.scrollY: auf iOS Safari ist
      // scrollTo() asynchron, ein direktes Zuruecklesen liefert dort noch
      // die alte Position. Der naechste Frame vergleicht dann die inzwischen
      // aktualisierte echte Position gegen diesen Wert -- mit einer
      // synchronen Lesung loeste das faelschlich die Abbruchsicherung aus
      // und liess die Animation nach ein paar Pixeln stehen bleiben.
      expectedY = currentY;
      window.requestAnimationFrame(step);
    }

    window.requestAnimationFrame(step);
  }

  document.addEventListener(
    'click',
    (event) => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return;
      }

      // event.target ist bei Klicks auf das Dokument selbst kein Element und
      // hat dann kein closest().
      const node = event.target;
      if (!node || typeof node.closest !== 'function') {
        return;
      }

      const target = node.closest('a[href*="#"]');

      // Dieselben Ausschluesse wie im Theme-Handler, damit wir nichts kapern,
      // was das Theme ohnehin durchgelassen haette.
      if (
        !target ||
        target.matches('[allow-hash-change]') ||
        target.pathname !== window.location.pathname ||
        target.search !== window.location.search
      ) {
        return;
      }

      const url = new URL(target.href);
      if (url.hash === '') {
        return;
      }

      // Hashes wie "#2" sind gueltige URL-Fragmente, aber keine gueltigen
      // CSS-Selektoren — querySelector wirft dann.
      let anchorElement = null;
      try {
        anchorElement = document.querySelector(url.hash);
      } catch (error) {
        return;
      }

      // Kein Ziel im Dokument: nicht anfassen. Das laesst insbesondere
      // "#shopifyReshowConsentBanner" im Footer unberuehrt, auf das Shopifys
      // Consent-Banner selbst hoert.
      if (!anchorElement) {
        return;
      }

      event.preventDefault();

      if (window.matchMedia('(prefers-reduced-motion: no-preference)').matches) {
        smoothScrollToElement(anchorElement);
      } else {
        anchorElement.scrollIntoView({ block: 'start', behavior: 'auto' });
      }

      document.documentElement.dispatchEvent(
        new CustomEvent('hashchange:simulate', { bubbles: true, detail: { hash: url.hash } })
      );
    },
    true // Capture: laeuft vor dem Delegate des Themes
  );

  /* ----------------------------------------------------------
     Magazin-Kategorien: die Links zeigen auf /blogs/<blog>/tagged/<tag>,
     also einen echten Seitenwechsel. Die Seite startet dadurch oben beim
     Banner, die gefilterte Artikelliste liegt weit darunter. Auf einer
     Tag-Seite scrollen wir deshalb direkt zur Liste.
     ---------------------------------------------------------- */

  function scrollToFilteredPosts() {
    if (!/\/blogs\/[^/]+\/tagged\//.test(window.location.pathname)) {
      return;
    }

    // Hash gewinnt, und bei zurueckgesetzter Scrollposition (Back-Button)
    // nicht dazwischenfunken.
    if (window.location.hash !== '' || window.scrollY > 50) {
      return;
    }

    const list = document.querySelector('.blog-tags') || document.querySelector('.blog-post-list');
    if (!list) {
      return;
    }

    const anchorElement = list.closest('.shopify-section') || list;
    const targetY = Math.max(
      0,
      anchorElement.getBoundingClientRect().top + window.scrollY - getAnchorScrollOffset(anchorElement)
    );
    window.scrollTo(0, targetY);
    return targetY;
  }

  // Bilder oberhalb der Liste laden nach und verschieben sie. Nach 'load'
  // deshalb nachkorrigieren, aber nur solange der Nutzer selbst nicht
  // gescrollt hat.
  function startFilteredPostsScroll() {
    // Im Editor wuerde jedes Neuladen der Vorschau vom bearbeiteten Banner wegspringen.
    if (window.Shopify?.designMode) return;
    let lastTargetY = scrollToFilteredPosts();
    if (lastTargetY === undefined) {
      return;
    }

    window.addEventListener(
      'load',
      () => {
        if (Math.abs(window.scrollY - lastTargetY) > 2) {
          return;
        }
        window.scrollTo(0, 0);
        lastTargetY = scrollToFilteredPosts();
      },
      { once: true }
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startFilteredPostsScroll);
  } else {
    startFilteredPostsScroll();
  }
})();

/**
 * Altersverifizierung: "Nein" muss den Zugriff wirklich sperren.
 * Der Vendor-Handler (assets/theme.js, AgeVerifierModal) blendet bei Klick auf
 * .age-verifier__decline-button nur die Fehler-Banner ein, laesst "Ja" aber
 * anklickbar -- ein Nutzer koennte die Ablehnung also folgenlos zuruecknehmen.
 * Wir haengen einen zweiten, unabhaengigen Handler an denselben Klick und
 * entfernen die Buttons komplett. Das Dialog-Element selbst laesst sich
 * ohnehin nicht schliessen (clickOutsideDeactivates/escapeDeactivates sind
 * fuer age-verifier-modal fest auf false gesetzt, siehe layout/theme.liquid
 * und assets/theme.js), die Banner-Nachricht bleibt damit die einzige
 * verbleibende Handlung.
 */
document.addEventListener('click', (event) => {
  const declineButton = event.target.closest('.age-verifier__decline-button');
  // Im Editor bleibt das Modal stehen und saehe ohne Buttons kaputt aus.
  if (!declineButton || window.Shopify?.designMode) return;

  declineButton.closest('.button-group')?.remove();
});

/**
 * Die App "EU Widerrufsbutton" haengt ihr Formular-Modal per JavaScript in die
 * Seite und nutzt dafuer h2/h3. Das Modal ist auf jeder Seite versteckt
 * vorhanden, taucht aber in der Ueberschriften-Struktur des Dokuments auf.
 * Hier werden die Tags durch div ersetzt; die Klassen der App bleiben erhalten,
 * die drei elementbasierten CSS-Regeln der App sind in custom.css gespiegelt.
 */
(() => {
  const CONTAINER_SELECTOR = '[class^="wrb-"], [class*=" wrb-"]';
  const HEADING_SELECTOR = 'h1, h2, h3, h4, h5, h6';

  const downgrade = (heading) => {
    const replacement = document.createElement('div');

    for (const attribute of heading.attributes) {
      replacement.setAttribute(attribute.name, attribute.value);
    }

    replacement.classList.add('wrb-heading', 'wrb-heading--' + heading.tagName.toLowerCase());
    replacement.innerHTML = heading.innerHTML;
    heading.replaceWith(replacement);
  };

  const scan = (root) => {
    if (!(root instanceof Element)) {
      return;
    }

    const containers = root.matches(CONTAINER_SELECTOR) ? [root] : [];
    containers.push(...root.querySelectorAll(CONTAINER_SELECTOR));

    for (const container of containers) {
      container.querySelectorAll(HEADING_SELECTOR).forEach(downgrade);
    }
  };

  const start = () => {
    scan(document.body);

    /* Die App haengt ihr Modal genau einmal an. Sobald es da war, gibt es
       nichts mehr zu beobachten; vorher lief der Beobachter fuer die ganze
       Seitenlaufzeit auf jeder DOM-Aenderung (Warenkorb-Drawer, Varianten). */
    const observer = new MutationObserver((mutations) => {
      let found = false;
      for (const mutation of mutations) {
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof Element)) return;
          if (node.matches(CONTAINER_SELECTOR) || node.querySelector(CONTAINER_SELECTOR)) found = true;
          scan(node);
        });
      }
      if (found) observer.disconnect();
    });
    observer.observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();

/**
 * Hinweistext im Cookie-Banner.
 *
 * Shopify liefert den Text des eigenen Consent-Banners aus und bietet dafuer
 * im Admin kein Textfeld; der Ersatz steht deshalb in den Theme-Einstellungen
 * (Eigene Einstellungen, Cookie-Hinweis). Der Standardsatz ("Wir und unsere Partner,
 * einschliesslich Shopify, verwenden notwendige Cookies sowie ...") stellt die
 * Partner voran und nennt die Freiwilligkeit des Widerrufs nicht.
 *
 * Ersetzt wird nur der Textknoten. Der Link zur Datenschutzerklaerung bleibt
 * dasselbe Element und behaelt Ziel, ID und Shopifys eigene Ereignisbindung.
 * Die Schaltflaechen und der Consent-Mechanismus bleiben unberuehrt.
 *
 * Inhaltlich bleiben alle Pflichtangaben der ersten Ebene erhalten:
 * Zweck der Cookies, Beteiligung von Shopify und weiteren Partnern,
 * Freiwilligkeit und Widerrufbarkeit der Einwilligung, Verweis auf die
 * Datenschutzerklaerung.
 *
 * Faellt das Skript aus, bleibt Shopifys Originaltext stehen. Der Ersatz
 * greift nur auf deutschsprachigen Seiten, weil er nicht uebersetzt vorliegt.
 */
(() => {
  let configured = '';
  try {
    configured = JSON.parse(document.getElementById('theme-brand-config')?.textContent || '{}').consentBannerTextDe || '';
  } catch (error) {
    // Ohne lesbare Konfiguration bleibt Shopifys Originaltext stehen.
  }

  // Leerzeichen vor dem angehaengten Datenschutz-Link; der Editor kuerzt es am Feldende weg.
  const TEXT = configured.trim() ? `${configured.trim()} ` : '';

  const replace = () => {
    const banner = document.getElementById('shopify-pc__banner');
    const paragraph = banner && banner.querySelector('.shopify-pc__banner__body p');

    if (!paragraph || paragraph.dataset.brandText === 'done') {
      return false;
    }

    const link = paragraph.querySelector('#shopify-pc__banner__body-policy-link');

    paragraph.textContent = '';
    paragraph.appendChild(document.createTextNode(TEXT));

    if (link) {
      paragraph.appendChild(link);
    }

    paragraph.dataset.brandText = 'done';
    return true;
  };

  const start = () => {
    if (!TEXT || !(document.documentElement.lang || '').toLowerCase().startsWith('de')) {
      return;
    }

    if (replace()) {
      return;
    }

    /* Shopify haengt das Banner erst nach dem Laden seines eigenen Skripts an
       <body>. Der Observer wartet darauf und beendet sich danach. */
    const observer = new MutationObserver(() => {
      if (replace()) {
        observer.disconnect();
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });

    /* Besucher mit erteilter Einwilligung bekommen kein Banner: dann liefe
       der Beobachter bis zum Verlassen der Seite. Shopify haengt das Banner
       innerhalb weniger Sekunden an, danach ist Warten zwecklos. */
    setTimeout(() => observer.disconnect(), 15000);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();

/**
 * Scrollposition beim Zurueckblaettern.
 *
 * Die Startseite besteht fast vollstaendig aus Video- und Bild-Sections. Der
 * Browser stellt die gespeicherte Position wieder her, sobald das Dokument
 * geparst ist -- zu diesem Zeitpunkt sind die Medien noch nicht geladen, die
 * Seite ist also kuerzer als vorher und die Position wird auf das aktuelle
 * Maximum gekappt, im Extremfall auf 0. Beim Wiederherstellen aus dem bfcache
 * tritt das nicht auf, deshalb ist der Fehler nicht auf jedem Zurueck-Weg
 * sichtbar.
 *
 * Korrigiert wird deshalb erst nach 'load', wenn die Hoehe steht, und nur bei
 * einer Rueckwaertsnavigation. Hat der Nutzer in der Zwischenzeit selbst
 * gescrollt, bleibt seine Position stehen.
 */
(() => {
  const STORAGE_PREFIX = 'brand:scroll:';
  const TOLERANCE = 4;

  /* Der Hash gehoert in den Schluessel: /seite#oben und /seite#unten sind
     verschiedene Einstiegspunkte und duerfen sich keine Position teilen. */
  const storageKey = () =>
    STORAGE_PREFIX + window.location.pathname + window.location.search + window.location.hash;

  const isBackForward = () => {
    const entry = performance.getEntriesByType('navigation')[0];
    return entry ? entry.type === 'back_forward' : performance.navigation?.type === 2;
  };

  const save = () => {
    try {
      sessionStorage.setItem(storageKey(), String(window.scrollY));
    } catch (error) {
      /* Privater Modus ohne sessionStorage: Standardverhalten bleibt. */
    }
  };

  const readSaved = () => {
    try {
      const value = parseInt(sessionStorage.getItem(storageKey()) || '', 10);
      return Number.isFinite(value) ? value : null;
    } catch (error) {
      return null;
    }
  };

  /* Nur pagehide: das Ereignis deckt Verlassen und Einfrieren gleichermassen
     ab. Ein zusaetzlicher beforeunload-Listener wuerde in Firefox und WebKit
     den bfcache verhindern -- also genau den Weg, der die Position ohne uns
     korrekt wiederherstellt. */
  window.addEventListener('pagehide', save);

  /* Die Tag-Seiten des Magazins scrollen ueber startFilteredPostsScroll (oben in
     dieser Datei) an die Artikelliste. Zwei Korrekturen wuerden sich gegenseitig
     ueberschreiben. */
  if (/\/blogs\/[^/]+\/tagged\//.test(window.location.pathname) || !isBackForward()) {
    return;
  }

  const saved = readSaved();
  if (saved === null || saved <= TOLERANCE) {
    return;
  }

  /* Nicht jeder Tastendruck und jede Beruehrung ist ein Scrollwunsch: Tab,
     Escape oder ein Tap auf einen Button haetten die Korrektur sonst gerade
     mobil regelmaessig verhindert. Gezaehlt wird deshalb echte Bewegung --
     eine Position, die weder der Ausgangspunkt noch das Ziel ist. */
  let userScrolled = false;
  const startY = window.scrollY;

  const watchScroll = () => {
    if (Math.abs(window.scrollY - startY) > TOLERANCE && Math.abs(window.scrollY - saved) > TOLERANCE) {
      userScrolled = true;
      window.removeEventListener('scroll', watchScroll);
    }
  };

  window.addEventListener('scroll', watchScroll, { passive: true });
  window.addEventListener('wheel', () => (userScrolled = true), { passive: true, once: true });

  window.addEventListener(
    'load',
    () => {
      if (userScrolled || Math.abs(window.scrollY - saved) <= TOLERANCE) {
        return;
      }

      window.scrollTo(0, saved);
      window.removeEventListener('scroll', watchScroll);
    },
    { once: true }
  );
})();

/**
 * Autoplay-Videos nach dem Zurueckblaettern wieder anwerfen.
 *
 * `video-media` (assets/theme.js, BaseMedia) merkt sich den Laufzustand im
 * Attribut `playing`, und `play()` bricht ab, solange dieses Attribut steht.
 * Kommt die Seite aus dem bfcache zurueck, ist das DOM samt Attribut
 * unveraendert, das Video selbst hat der Browser aber angehalten. Der Zustand
 * ist damit verklemmt: Das Attribut behauptet "laeuft", das Video steht, und
 * kein Scrollen weckt es wieder auf, weil der IntersectionObserver dieselbe
 * blockierte play()-Methode aufruft.
 *
 * Sichtbar wird das als weisse Flaeche: Ein `video` zeigt sein Poster nur bis
 * zum ersten Frame. Danach bleibt bei angehaltenem Video und verworfenem
 * Frame-Puffer nur der Sectionhintergrund uebrig.
 *
 * Ob das Attribut den Sprung ueberlebt, haengt davon ab, ob der Browser beim
 * Einfrieren noch ein `pause`-Ereignis zustellt. Faellt es aus, steht das
 * Attribut und play() blockt; kommt es an, raeumt das Theme das Attribut zwar
 * ab, aber niemand startet neu, weil der IntersectionObserver fuer ein
 * dauerhaft sichtbares Element nicht erneut ausloest. Beide Wege enden bei
 * demselben stehenden Video, deshalb pruefen wir das `video` und nicht das
 * Attribut.
 *
 * Wir raeumen deshalb das Attribut ab und starten neu. Verweigert der Browser
 * das Autoplay, holt `load()` wenigstens das Posterbild zurueck.
 */
(() => {
  const isVisible = (element) => {
    const rect = element.getBoundingClientRect();
    return rect.bottom > 0 && rect.top < window.innerHeight && element.offsetParent !== null;
  };

  const openAgeGate = () => document.querySelector('age-verifier-modal[open]');

  /* Ein Video pro Durchlauf nur einmal anstossen. Ohne diese Sperre laufen die
     Nachfassdurchlaeufe in ein noch offenes play()-Promise; `load()` im
     Fehlerfall bricht dieses Promise mit AbortError ab, dessen catch wieder
     load() ruft -- jeder Lauf setzt das Video auf Sekunde 0 und laedt neu. */
  const pending = new WeakSet();

  const resync = () => {
    /* Solange die Altersverifizierung laeuft, sieht der Nutzer nur deren
       eigenes Video. Alles andere wuerde ihm nur die Bandbreite wegnehmen. */
    if (document.hidden || openAgeGate()) {
      return;
    }

    document.querySelectorAll('video-media[autoplay]').forEach((media) => {
      const video = media.querySelector('video');

      /* Das Video der Altersverifizierung gehoert dem Modal: solange es offen
         ist, laeuft es ohnehin, danach soll es gerade nicht weiterlaufen. Das
         geschlossene Modal bleibt im DOM und gilt sonst als sichtbar. */
      if (media.closest('age-verifier-modal')) {
        return;
      }

      if (!video || !video.paused || pending.has(video) || !isVisible(media)) {
        return;
      }

      media.removeAttribute('playing');
      pending.add(video);

      const player = video.play();

      if (!player) {
        pending.delete(video);
        return;
      }

      player
        .catch((error) => {
          /* AbortError heisst: ein anderer Aufruf hat uebernommen. Nur bei
             verweigertem Autoplay das Posterbild zurueckholen. */
          if (error.name !== 'AbortError') {
            video.load();
          }
        })
        .finally(() => pending.delete(video));
    });
  };

  /* Chrome haelt das Video teils erst nach dem pageshow an, ein einmaliger
     Durchlauf trifft dann noch das laufende Video und tut nichts. Deshalb
     zweimal nachfassen; laeuft alles, sind beide Durchlaeufe wirkungslos.
     Anstehende Timer werden vorher verworfen, sonst stapeln sich die Laeufe
     bei mehrfachem Tabwechsel. */
  let timers = [];

  const resyncSoon = () => {
    timers.forEach(clearTimeout);
    timers = [setTimeout(resync, 300), setTimeout(resync, 1200)];
    resync();
  };

  window.addEventListener('pageshow', (event) => {
    if (event.persisted) {
      resyncSoon();
    }
  });

  /* Ohne bfcache stellt derselbe Zustand sich ein, wenn der Tab im
     Hintergrund lag: Chrome haelt Videos dort an, das Attribut bleibt. */
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) {
      resyncSoon();
    }
  });

  /* Zurueck-Navigation ohne bfcache-Treffer: die Seite wird neu aufgebaut,
     das Video kann aber im Hintergrund-Tab schon wieder stehen. */
  window.addEventListener('popstate', resyncSoon);

  /**
   * Preloading erst kurz vor dem Viewport.
   *
   * assets/theme.js stuft jedes Video 800px vor dem Viewport auf preload="auto"
   * hoch und ueberschreibt damit den Wert aus dem Liquid. Auf der Startseite
   * liegen mehrere Videosektionen dicht beieinander: schon beim ersten Rendern
   * beginnen dadurch mehrere gleichzeitig vollstaendig zu laden und nehmen dem
   * sichtbaren Video die Leitung weg.
   *
   * theme.js ist eine Vendor-Datei und bleibt unveraendert. Stattdessen wird das
   * verfruehte "auto" hier zurueckgenommen, bis das Video wirklich nahe am
   * Viewport ist. Danach traegt sich der Beobachter aus und theme.js behaelt das
   * letzte Wort -- ein einmal freigegebenes Video wird nicht wieder gedrosselt.
   *
   * Bewusst nur fuer autoplay-Videos: ein Video, das der Nutzer selbst startet,
   * soll ohne Wartezeit anspringen.
   */
  const PRELOAD_MARGIN = 200;

  /* Der Zustand wird aus der Position abgeleitet, nicht aus dem preload-Attribut.
     theme.js laeuft vor custom.js, sein Beobachter kann also schon "auto"
     gesetzt haben -- der vorgefundene Wert sagt daher nichts darueber aus, was
     das Liquid gemeint hat.

     Wichtig fuer den Hero: der ist beim Laden bereits sichtbar und wird hier gar
     nicht angefasst. Wuerde er kurz auf "metadata" zurueckgesetzt, koennte der
     Browser den laufenden Abruf abbrechen -- genau das Gegenteil des Ziels. */
  const nearViewport = (element) => {
    const rect = element.getBoundingClientRect();
    return rect.bottom > -PRELOAD_MARGIN && rect.top < window.innerHeight + PRELOAD_MARGIN;
  };

  document.querySelectorAll('video-media[autoplay]').forEach((media) => {
    const video = media.querySelector('video');

    if (!video || media.closest('age-verifier-modal') || nearViewport(media)) {
      return;
    }

    video.setAttribute('preload', 'none');

    /* theme.js setzt das Attribut ueber setAttribute, nicht ueber die Property:
       ein MutationObserver ist die einzige Stelle, die das mitbekommt. */
    const guard = new MutationObserver(() => {
      if (video.getAttribute('preload') === 'auto') {
        video.setAttribute('preload', 'none');
      }
    });

    guard.observe(video, { attributeFilter: ['preload'] });

    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) {
        return;
      }

      /* Erst abtreten, dann freigeben: sonst nimmt der eigene Beobachter die
         Freigabe sofort wieder zurueck. */
      guard.disconnect();
      observer.disconnect();
      video.setAttribute('preload', 'auto');
    }, { rootMargin: `${PRELOAD_MARGIN}px` });

    observer.observe(media);
  });

  /**
   * Bandbreite fuer die Altersverifizierung freiraeumen.
   *
   * Das Hintergrundvideo der Altersverifizierung ist dieselbe 1080p-Datei mit
   * 7,2 Mbit/s, die auch der Hero der Startseite verwendet, und beide starten
   * gleichzeitig mit `preload="auto"`; dazu holen vier weitere Video-Sections
   * ihre Metadaten. Der Nutzer sieht in diesem Moment aber ausschliesslich das
   * Modal -- alles dahinter konkurriert nur um dieselbe Leitung und laesst das
   * sichtbare Video stocken.
   *
   * Wir halten die verdeckten Videos deshalb an und setzen ihr Preloading aus,
   * bis das Modal geschlossen ist. Danach wird der vorherige Zustand
   * wiederhergestellt und der normale Anlauf uebernimmt.
   */
  const gate = document.querySelector('age-verifier-modal');

  /* Der Editor oeffnet und ersetzt das Modal bei jeder Einstellung, ohne dass
     release() laeuft: die Drosselung bliebe haengen und hielte jedes Video an. */
  if (!gate || window.Shopify?.designMode) {
    return;
  }

  const previousPreload = new Map();
  let suppressing = false;

  const suppressAll = () => {
    if (suppressing) {
      return;
    }

    suppressing = true;

    document.querySelectorAll('video').forEach((video) => {
      if (gate.contains(video)) {
        return;
      }

      if (!previousPreload.has(video)) {
        previousPreload.set(video, video.getAttribute('preload'));
      }

      video.preload = 'none';
      video.pause();
    });

    document.addEventListener('play', suppress, true);
  };

  /* Das Theme setzt `preload` und startet die Wiedergabe erst, wenn sein
     IntersectionObserver ausloest -- also nach diesem Durchlauf. Ein einmaliges
     Anhalten wuerde deshalb sofort wieder ueberschrieben. `play` steigt nicht
     auf, der Listener haengt darum in der Capture-Phase am Dokument. */
  const suppress = (event) => {
    const video = event.target;

    if (!(video instanceof HTMLVideoElement) || gate.contains(video)) {
      return;
    }

    video.pause();
    video.preload = 'none';
  };

  /**
   * Uebergabe an das Video dahinter.
   *
   * Der Hero der Startseite spielt dieselbe Datei wie die Altersverifizierung,
   * stand waehrend des Gates aber angehalten auf Sekunde 0. Ohne Abgleich
   * springt das Bild beim Schliessen an den Anfang zurueck. Wir setzen das
   * verdeckte Video deshalb auf die Position des Gate-Videos, bevor die Blende
   * laeuft -- der Nutzer sieht denselben Bildausschnitt weiter.
   *
   * Verglichen wird `currentSrc`: nur ein Video mit derselben Quelle kann
   * ueberhaupt nahtlos uebernehmen, alles andere bleibt unangetastet.
   */
  const handoff = () => {
    const source = gate.querySelector('video');

    if (!source || !source.currentSrc) {
      return;
    }

    const position = source.currentTime;

    document.querySelectorAll('video').forEach((video) => {
      if (gate.contains(video) || video.currentSrc !== source.currentSrc) {
        return;
      }

      const seek = () => {
        const duration = video.duration;

        if (Number.isFinite(duration) && duration > 0) {
          try {
            video.currentTime = position % duration;
          } catch (error) {
            /* Browser verweigert den Sprung: dann eben von vorn. */
          }
        }

        video.play()?.catch(() => {});
      };

      /* Nach der Drosselung kann noch nicht einmal die Dauer bekannt sein --
         ein Sprung waere dann wirkungslos. */
      if (video.readyState >= 1) {
        seek();
      } else {
        video.addEventListener('loadedmetadata', seek, { once: true });
      }
    });
  };

  /**
   * Blende statt Hartschnitt.
   *
   * `DialogElement.hide()` (assets/theme.js) kennt keine Ausblendanimation
   * (createLeaveAnimationControls mit duration 0) und setzt display:none direkt
   * per Inline-Style. Die Klasse haelt das Modal fuer die Dauer der Blende
   * sichtbar (die Regel in assets/custom.css sticht den Inline-Style mit
   * !important) und nimmt es danach wieder aus dem Weg.
   */
  const FADE_MS = 700;
  let fadeTimer;

  const fadeOut = () => {
    clearTimeout(fadeTimer);
    gate.classList.add('age-verifier--closing');

    /* Zwei Frames: im ersten wird das Modal ueberhaupt erst wieder sichtbar
       gehalten, erst die Aenderung im zweiten loest den Uebergang aus. */
    requestAnimationFrame(() => {
      requestAnimationFrame(() => gate.classList.add('age-verifier--faded'));
    });

    fadeTimer = setTimeout(() => {
      gate.classList.remove('age-verifier--closing', 'age-verifier--faded');

      /* Das Modal bleibt nach dem Schliessen im DOM, nur verdeckt. Sein Video
         weiterlaufen zu lassen, waere dieselbe Verschwendung umgekehrt. */
      gate.querySelectorAll('video').forEach((video) => video.pause());
    }, FADE_MS);
  };

  const release = () => {
    if (!suppressing) {
      return;
    }

    suppressing = false;
    document.removeEventListener('play', suppress, true);

    previousPreload.forEach((value, video) => {
      if (value === null) {
        video.removeAttribute('preload');
      } else {
        video.preload = value;
      }
    });

    handoff();
    fadeOut();
    resyncSoon();
  };

  /* Beide Richtungen beobachten: das Modal kann auch erst nach diesem Skript
     oeffnen. Ohne den Hinweg bliebe die Drosselung dann ganz aus, ohne den
     Rueckweg wuerde sie nie enden. */
  new MutationObserver(() => {
    if (gate.hasAttribute('open')) {
      suppressAll();
    } else {
      release();
    }
  }).observe(gate, { attributes: true, attributeFilter: ['open'] });

  if (gate.hasAttribute('open')) {
    suppressAll();
  }
})();

/* Newsletter im Footer: Anmeldung geht an die HubSpot Forms API, damit alle
   Kontakte im selben Formular landen wie der Embed auf der Startseite. */
(() => {
  const readCookie = (name) => {
    const match = document.cookie.match(new RegExp('(^|;\\s*)' + name + '=([^;]*)'));
    return match ? decodeURIComponent(match[2]) : undefined;
  };

  /* HubSpot meldet nicht zurueck, ob eine Adresse schon Kontakt ist (die API
     antwortet immer mit 200). Wir merken uns die Anmeldung deshalb lokal und
     ersparen dem Nutzer wenigstens die zweite Absendung im selben Browser.
     Gespeichert wird nur ein Hash, keine E-Mail-Adresse im Klartext. */
  const STORAGE_KEY = 'newsletter:subscribed:v2';

  try {
    window.localStorage.removeItem('newsletter:subscribed');
  } catch {
    /* Privater Modus: nichts zu raeumen. */
  }

  const hashAddress = async (email) => {
    try {
      const bytes = new TextEncoder().encode(email);
      const hash = await window.crypto.subtle.digest('SHA-256', bytes);
      return [...new Uint8Array(hash)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
    } catch {
      /* Ohne SubtleCrypto (kein HTTPS, alter Browser) entfaellt die Duplikatserkennung. */
      return null;
    }
  };

  const readSubscribed = () => {
    try {
      const stored = JSON.parse(window.localStorage.getItem(STORAGE_KEY));
      return Array.isArray(stored) ? stored : [];
    } catch {
      return [];
    }
  };

  const rememberSubscribed = (hash) => {
    if (!hash) return;

    try {
      const stored = readSubscribed();
      if (!stored.includes(hash)) {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify([...stored, hash]));
      }
    } catch {
      /* Privater Modus oder voller Speicher: nicht der Rede wert. */
    }
  };

  const showStatus = (form, kind) => {
    const holder = form.querySelector('[data-newsletter-status]');
    if (!holder) return;

    const template = holder.querySelector(`[data-newsletter-status-${kind}]`);
    if (!template) return;

    holder.querySelectorAll(':scope > :not(template)').forEach((node) => node.remove());
    holder.appendChild(template.content.cloneNode(true));
    holder.hidden = false;

    /* Der Container kommt aus hidden, eine Live-Region allein meldet dann
       nichts. Fokus verschieben, bevor hideFields() den geklickten Button
       entfernt und der Fokus auf <body> faellt. */
    holder.focus();
  };

  /* Delegiert, damit das Formular auch nach einem Section-Rerender im
     Theme-Editor weiter funktioniert. */
  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('form[data-hubspot-newsletter]');
    if (!form) return;

    event.preventDefault();

    const portalId = form.dataset.portalId;
    const formId = form.dataset.hubspotFormId;
    if (!portalId || !formId) return;

    if (!form.reportValidity()) return;

    const region = form.dataset.region || 'eu1';
    const endpoint = `https://api-${region}.hsforms.com/submissions/v3/integration/submit/${portalId}/${formId}`;

    const button = form.querySelector('button[type="submit"]');
    const email = form.querySelector('input[type="email"]');
    /* Bei consent-mode "button" ist der Klick auf den Button die Einwilligung,
       der Text dazu steht sichtbar daneben. Double-Opt-in laeuft in HubSpot. */
    const optIn = form.querySelector('input[name="opt_in"]') || form.dataset.consentMode === 'button';
    const address = email.value.trim().toLowerCase();

    const hideFields = () => {
      form.querySelector('.form-row')?.setAttribute('hidden', '');
      form.querySelector('.checkbox-control')?.setAttribute('hidden', '');
      form.querySelector('.newsletter-form__consent')?.setAttribute('hidden', '');
    };

    /* Honeypot ausgefuellt: Erfolg anzeigen, damit der Bot nicht nachjustiert, aber nichts senden. */
    if (form.querySelector('input[name="hp_field"]')?.value) {
      showStatus(form, 'success');
      hideFields();
      return;
    }

    /* Je Formular merken: wer den Newsletter hat, ist damit noch nicht fuer
       einen Presale mit eigenem HubSpot-Formular registriert. */
    const addressHash = await hashAddress(`${formId}:${address}`);

    if (addressHash && readSubscribed().includes(addressHash)) {
      showStatus(form, 'duplicate');
      hideFields();
      return;
    }

    button?.setAttribute('aria-busy', 'true');

    const fields = [{ objectTypeId: '0-1', name: 'email', value: address }];

    if (optIn) {
      fields.push({ objectTypeId: '0-1', name: 'opt_in', value: 'true' });
    }

    /* Einwilligungstext und Zeitpunkt gehen mit an HubSpot, damit die
       Einwilligung dort nachweisbar ist und nicht nur als Flag ankommt. */
    const consentText = form.dataset.consentText;
    const payload = {
      fields,
      context: {
        hutk: readCookie('hubspotutk'),
        pageUri: window.location.href,
        pageName: document.title,
      },
    };

    if (optIn && consentText) {
      payload.legalConsentOptions = {
        consent: {
          consentToProcess: true,
          text: consentText,
        },
      };
    }

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error(`HubSpot: ${response.status}`);

      rememberSubscribed(addressHash);
      showStatus(form, 'success');
      hideFields();
    } catch (error) {
      console.error(error);
      showStatus(form, 'error');
    } finally {
      button?.removeAttribute('aria-busy');
    }
  });
})();

/* Zwei-Klick-Loesung fuer externe Einbettungen: das Drittanbieter-Skript wird
   erst nach ausdruecklichem Klick nachgeladen, vorher steht nur der Platzhalter. */
(() => {
  const loadScript = (src) =>
    new Promise((resolve, reject) => {
      const existing = document.querySelector(`script[src="${src}"]`);
      if (existing) return resolve();

      const tag = document.createElement('script');
      tag.src = src;
      tag.async = true;
      tag.onload = resolve;
      tag.onerror = reject;
      document.head.appendChild(tag);
    });

  const builders = {
    hubspot(host) {
      const region = host.dataset.region || 'eu1';
      const frame = document.createElement('div');
      frame.className = 'hs-form-frame';
      frame.dataset.region = region;
      frame.dataset.formId = host.dataset.formId;
      frame.dataset.portalId = host.dataset.portalId;
      host.appendChild(frame);
      /* na1 laeuft ohne Regionspraefix, alle anderen Regionen mit. */
      const origin = region === 'na1' ? 'js.hsforms.net' : `js-${region}.hsforms.net`;
      return loadScript(`https://${origin}/forms/embed/${host.dataset.portalId}.js`);
    },

    calendly(host) {
      const widget = document.createElement('div');
      widget.className = 'calendly-inline-widget';
      widget.dataset.url = host.dataset.url;
      widget.style.minWidth = '320px';
      widget.style.height = getComputedStyle(host).getPropertyValue('--consent-embed-height').trim() || '700px';
      host.appendChild(widget);
      return loadScript('https://assets.calendly.com/assets/external/widget.js');
    },
  };

  document.addEventListener('click', async (event) => {
    const button = event.target.closest('.consent-embed__load');
    if (!button) return;

    const host = button.closest('[data-consent-embed]');
    const build = host && builders[host.dataset.consentEmbed];
    if (!build) return;

    button.setAttribute('aria-busy', 'true');

    /* Platzhalter zuerst entfernen, sonst rendert das Widget darunter weiter. */
    host.querySelector('.consent-embed__placeholder')?.remove();

    try {
      await build(host);
      host.setAttribute('data-loaded', '');
    } catch (error) {
      console.error(error);
      host.textContent = '';
    }
  });
})();

/* ============================================================
   Trusted-Shops-Badge: Selbstrueckzug und Consent-Banner.

   Drawer, Modals und die mobile Kaufleiste erledigt custom.css ueber `:has()`
   ohne JavaScript. Hier stehen die beiden Faelle, die sich nicht aus dem DOM
   ablesen lassen.

   1. Nach zehn sichtbaren Sekunden gleitet das Badge nach links aus dem Bild.
      Die Uhr laeuft nur, solange es wirklich zu sehen ist, und haelt an,
      wenn Consent-Banner oder ein Drawer davor liegen -- das Widget laedt
      ohnehin erst nach `window.onload`.
      Der Ruecktritt wird in der `sessionStorage` vermerkt, damit es nicht auf
      jeder weiteren Seite erneut zehn Sekunden lang auftaucht. Ein Reload
      derselben Seite hebt den Vermerk auf: wer neu laedt, will die Seite so
      sehen, wie sie ein frischer Besucher sieht.

   2. Solange der Consent-Banner steht, ist das Badge weg: der Banner sitzt
      unten links auf derselben Hoehe und traegt Pflichttext. Ob Shopify ihn
      nach der Wahl entfernt oder nur ausblendet, ist nicht zugesichert,
      deshalb wird die Sichtbarkeit gemessen statt das Element gezaehlt. Zwei
      gleiche Messungen hintereinander, bevor umgeschaltet wird -- der Banner
      blendet animiert ein, ein einzelner Messwert mitten in der Animation
      wuerde das Badge sonst kurz aufblitzen lassen.
   ============================================================ */
(() => {
  'use strict';

  const root = document.documentElement;
  const VISIBLE_MS = 10000;
  const STORAGE_KEY = 'trustbadge-eingeklappt';

  /* Privater Modus kann jeden Zugriff werfen, nicht nur das Schreiben. */
  const remember = {
    get() {
      try {
        return sessionStorage.getItem(STORAGE_KEY) === '1';
      } catch {
        return false;
      }
    },
    set() {
      try {
        sessionStorage.setItem(STORAGE_KEY, '1');
      } catch {
        /* dann eben nur fuer diese Seite */
      }
    },
    clear() {
      try {
        sessionStorage.removeItem(STORAGE_KEY);
      } catch {
        /* nichts zu raeumen */
      }
    },
  };

  const retreat = () => {
    root.classList.add('trustbadge-idle');
    remember.set();
  };

  /* `navigate` steht auch fuer den allerersten Aufruf, `back_forward` fuer
     Zurueck/Vorwaerts -- beides zaehlt als Durchnavigieren, nur `reload`
     nicht. */
  const neuGeladen = performance.getEntriesByType('navigation')[0]?.type === 'reload';
  // Im Editor bliebe das Badge sonst die ganze Sitzung eingeklappt; dort wie beim ersten Besuch zeigen.
  const designMode = Boolean(window.Shopify?.designMode);

  if (neuGeladen || designMode) {
    remember.clear();
  } else if (remember.get()) {
    root.classList.add('trustbadge-idle');
  }

  let lastReading = null;
  let sichtbarMs = 0;
  let letzterTakt = null;

  const bannerVisible = () => {
    const banner = document.getElementById('shopify-pc__banner');
    return !!banner && banner.getBoundingClientRect().height > 0;
  };

  /* Gezaehlt wird die tatsaechlich sichtbare Zeit, nicht die Zeit seit dem
     Laden: liegt das Badge hinter dem Consent-Banner oder einem Drawer, saehe
     der Besucher es sonst nie. Ueber `visibility` gemessen, weil dort jede
     Regel aus custom.css zusammenlaeuft. `document.hidden` gehoert dazu: eine
     im Hintergrund geoeffnete Seite hat das Badge nie gezeigt. */
  const badgeSichtbar = () => {
    if (document.hidden) return false;
    const el = document.querySelector('[id^="trustbadge-container-"] > div > div');
    return !!el && getComputedStyle(el).visibility === 'visible';
  };

  const tick = () => {
    if (root.classList.contains('trustbadge-idle')) {
      clearInterval(poll);
      return;
    }

    const reading = bannerVisible();
    if (reading === lastReading) {
      root.classList.toggle('trustbadge-consent', reading);
    }
    lastReading = reading;

    const jetzt = performance.now();
    if (badgeSichtbar() && letzterTakt !== null) {
      sichtbarMs += jetzt - letzterTakt;
    }
    letzterTakt = jetzt;

    if (!designMode && sichtbarMs >= VISIBLE_MS) {
      retreat();
    }
  };

  /* Laeuft, bis das Badge weg ist -- keine feste Frist: wer den Consent-Banner
     lange stehen laesst, soll das Badge trotzdem noch zu sehen bekommen. Die
     Notbremse nach fuenf Minuten fuer den Fall, dass das Widget gar nicht
     kommt und `trustbadge-idle` deshalb nie gesetzt wird. */
  const poll = setInterval(tick, 500);
  setTimeout(() => clearInterval(poll), 300000);

  document.addEventListener('visitorConsentCollected', () => {
    lastReading = null;
    setTimeout(tick, 300);
    setTimeout(tick, 700);
  });

  tick();
})();

/* ---------------------------------------------------------------------------
   Modal fuer Galerie-Videos auf der Produktseite.

   Die Lightbox des Themes (PhotoSwipe) nimmt ausschliesslich <img>-Elemente auf
   -- theme.js baut ihre Datenquelle aus `cell.querySelectorAll(':scope > img')`
   und filtert danach nochmals auf data-media-type="image". Ein Video kann dort
   nicht landen. Die Kachel bekommt deshalb eine eigene Klickflaeche, die das im
   Theme vorhandene <x-modal> per aria-controls oeffnet.

   Das <video-media> wird beim Oeffnen in das Modal verschoben statt dort neu
   gerendert -- die laufende Wiedergabe (Zeitposition, Ladezustand) bleibt so
   erhalten. dialog:before-show feuert, bevor x-modal sich selbst nach <body>
   verschiebt; die Kachel ist an dem Punkt noch das Elternelement.
   --------------------------------------------------------------------------- */
(() => {
  const restore = new WeakMap();

  /* theme.js setzt preload="auto" auf jedes <video-media> in Sichtnaehe und holt
     damit die ganze Datei (2-3 MB) beim Seitenaufbau. Frueher wurde das
     verhindert, indem die <source> ganz aus dem DOM blieb -- dann wusste der
     Browser bis zum Antippen nichts vom Video und musste bei null anfangen:
     sekundenlang networkState 2 bei readyState 0, es sah aus als spiele nichts
     ab. Jetzt steht die Quelle drin und nur der Dateikopf wird geholt; das
     erste Bild ist beim Antippen da, der Rest streamt nach. */
  const haltePreload = (video) => {
    if (video.getAttribute('preload') === 'auto' && video.paused && video.currentTime === 0) {
      video.setAttribute('preload', 'metadata');
    }
  };

  const sichern = () => {
    document.querySelectorAll('.product-gallery__media video').forEach((video) => {
      if (video.dataset.preloadGesichert) return;
      video.dataset.preloadGesichert = '1';
      haltePreload(video);
      new MutationObserver(() => haltePreload(video)).observe(video, {
        attributes: true,
        attributeFilter: ['preload'],
      });
    });
  };

  sichern();
  document.addEventListener('shopify:section:load', sichern);
  addEventListener('pageshow', sichern);

  const onBeforeShow = (event) => {
    const modal = event.target;
    if (!modal.matches?.('[data-video-modal]')) return;

    const tile = modal.closest('.product-gallery__media');
    const videoMedia = tile?.querySelector('video-media');
    const video = videoMedia?.querySelector('video');
    if (!videoMedia || !video) return;

    restore.set(modal, {
      videoMedia,
      parent: videoMedia.parentElement,
      nextSibling: videoMedia.nextElementSibling,
      controls: video.controls,
      muted: video.muted,
    });

    modal.append(videoMedia);
    video.controls = true;
    video.muted = false;
    if (video.paused) {
      video.play().catch(() => {});
    }
  };

  const onAfterHide = (event) => {
    const modal = event.target;
    const state = restore.get(modal);
    if (!state) return;
    restore.delete(modal);

    state.parent.insertBefore(state.videoMedia, state.nextSibling);

    const video = state.videoMedia.querySelector('video');
    if (video) {
      video.controls = state.controls;
      video.muted = state.muted;
    }
  };

  document.addEventListener('dialog:before-show', onBeforeShow, true);
  document.addEventListener('dialog:after-hide', onAfterHide, true);
})();

/* ----------------------------------------------------------
   Kapitel auf der Produktseite: Parallax und Reveal.

   Kein Scroll-Hijacking (Lenis o.ae.): das kollidiert mit der Sticky-
   Kaufleiste, dem Anker-Scroll oben in dieser Datei und iOS. Die Bilder
   laufen beim Scrollen langsamer als die Seite, die Texte blenden einmal
   ein. Beides ueber rAF gedrosselt und nur fuer sichtbare Elemente
   berechnet; bei prefers-reduced-motion passiert nichts.
   Die CSS-Seite steht in custom.css, Abschnitt "Kapitel".
   ---------------------------------------------------------- */
(() => {
  'use strict';

  if (!document.body.classList.contains('template-product')) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  /* Die wein-*-Sections schalten beides per Setting und setzen dafuer
     data-parallax / data-reveal. Die Theme-Sections (Bild/Text-Split, Rich-Text,
     Custom-Liquid-Zitat) haben kein solches Setting und laufen weiter ueber
     Selektoren; Elemente in einer wein-Section ([data-kapitel]) sind davon
     ausgenommen, sonst waere das Ausschalten dort wirkungslos. */
  const PARALLAX_THEME = [
    '.section-split__column--image .content-box__background-media',
  ];
  const REVEAL_THEME = [
    '.section-split__column:not(.section-split__column--image) .placement-grid',
    '.wein-zitat',
    '.wein-autor',
    '.shopify-section--rich-text .rich-text',
  ];
  const PARALLAX_RANGE = 0.08; // Anteil der Viewport-Hoehe, den das Bild gegenlaeuft

  const designMode = Boolean(window.Shopify?.designMode);

  const collect = (scope, attribute, themeSelectors) => [
    ...scope.querySelectorAll(`[${attribute}]`),
    ...[...scope.querySelectorAll(themeSelectors.join(','))].filter((el) => !el.closest('[data-kapitel]')),
  ];

  const visible = new Set();
  let ticking = false;

  const update = () => {
    ticking = false;
    const vh = window.innerHeight;
    visible.forEach((el) => {
      if (!el.isConnected) {
        visible.delete(el);
        return;
      }
      const r = el.getBoundingClientRect();
      // -1 (unterhalb) .. 0 (Mitte) .. 1 (oberhalb); Bild laeuft in Gegenrichtung
      const progress = ((r.top + r.height / 2) - vh / 2) / vh;
      const shift = Math.max(-1, Math.min(1, progress)) * -PARALLAX_RANGE * vh;
      el.style.setProperty('--py', shift.toFixed(1) + 'px');
    });
  };

  const request = () => {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(update);
  };

  const io = new IntersectionObserver((entries) => {
    entries.forEach((e) => (e.isIntersecting ? visible.add(e.target) : visible.delete(e.target)));
    request();
  }, { rootMargin: '20% 0px' });

  const reveal = new IntersectionObserver((entries, obs) => {
    entries.forEach((e) => {
      if (!e.isIntersecting) return;
      e.target.classList.add('is-inview');
      obs.unobserve(e.target);
    });
  }, { threshold: 0.15 });

  let listening = false;

  /* Pro Scope, weil der Theme-Editor Sections einzeln neu rendert und die
     neuen Elemente sonst ohne Klassen (und ohne Vergroesserung) dastuenden. */
  const init = (scope) => {
    const wrappers = collect(scope, 'data-parallax', PARALLAX_THEME).filter((el) => !el.classList.contains('wein-parallax'));
    wrappers.forEach((el) => {
      el.classList.add('wein-parallax');
      io.observe(el);
    });

    if (wrappers.length && !listening) {
      listening = true;
      window.addEventListener('scroll', request, { passive: true });
      window.addEventListener('resize', request, { passive: true });
    }

    collect(scope, 'data-reveal', REVEAL_THEME).forEach((el) => {
      if (el.classList.contains('wein-reveal')) return;
      el.classList.add('wein-reveal');
      // Im Editor den Endzustand zeigen: ein Einblenden erst beim Scrollen verbirgt, was man gerade einstellt.
      if (designMode) el.classList.add('is-inview');
      else reveal.observe(el);
    });
  };

  init(document);

  if (designMode) {
    document.addEventListener('shopify:section:load', (event) => init(event.target));
    document.addEventListener('shopify:section:unload', (event) => {
      event.target.querySelectorAll('.wein-parallax').forEach((el) => {
        io.unobserve(el);
        visible.delete(el);
      });
    });
  }
})();

/* ------------------------------------------------------------
   Bilderstrecke im Artikeltext (snippets/article-slideshow.liquid).
   Erbt von effect-carousel aus theme.js: Autoplay, Wischen, Tastatur und
   Pause bei verborgenem Tab kommen vom Theme. Eigen sind die ruhigere
   Ueberblendung, die Fortschrittslinie und die Pause bei Hover und ausserhalb
   des sichtbaren Bereichs. Bei reduzierter Bewegung laeuft nichts von selbst.
   ------------------------------------------------------------ */
customElements.whenDefined('effect-carousel').then(() => {
  'use strict';
  if (customElements.get('article-slideshow')) return;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  class ArticleSlideshow extends customElements.get('effect-carousel') {
    #ready = false;

    connectedCallback() {
      super.connectedCallback();
      if (this.#ready || !this.player) return;
      this.#ready = true;

      if (reducedMotion.matches) {
        this.player.stop();
        return;
      }

      const bar = this.parentElement.querySelector('.article-slideshow__progress > span');
      let run = null;
      this.player.addEventListener('player:start', (event) => {
        run?.cancel();
        run = bar?.animate({ transform: ['scaleX(0)', 'scaleX(1)'] }, { duration: event.detail.duration * 1000, fill: 'forwards' });
      });
      this.player.addEventListener('player:pause', () => run?.pause());
      this.player.addEventListener('player:resume', () => run?.play());
      this.player.addEventListener('player:stop', () => run?.cancel());

      // Das pause-on-hover des Themes setzt auch nach einer Pause per Knopf
      // wieder ein. Hier hebt jeder Grund nur seine eigene Pause auf.
      const holds = new Set();
      const hold = (reason, active) => {
        if (active) {
          if (!this.player.paused || holds.size) {
            this.player.pause();
            holds.add(reason);
          }
        } else if (holds.delete(reason) && !holds.size) {
          this.player.resume();
        }
      };
      this.addEventListener('mouseenter', () => hold('hover', true));
      this.addEventListener('mouseleave', () => hold('hover', false));
      new IntersectionObserver(([entry]) => hold('viewport', !entry.isIntersecting), { threshold: 0.25 }).observe(this);
    }

    createOnChangeAnimationControls(fromSlide, toSlide) {
      const timing = { duration: reducedMotion.matches ? 0 : 1200, easing: 'cubic-bezier(.45, 0, .2, 1)', fill: 'forwards' };
      const animations = [fromSlide.animate({ opacity: [1, 0] }, timing), toSlide.animate({ opacity: [0, 1] }, timing)];
      const done = Promise.all(animations.map((a) => a.finished))
        .then(() => animations.forEach((a) => { a.commitStyles(); a.cancel(); }))
        .catch(() => {});
      done.complete = () => animations.forEach((a) => a.finish());
      return done;
    }
  }

  customElements.define('article-slideshow', ArticleSlideshow);
});

/* ------------------------------------------------------------
   Versandkosten-Aktion: prueft die Zeitfenster aus
   snippets/versandaktion.liquid im Browser nach. Liquid entscheidet
   beim Rendern; eine gecachte Seite kennt das Aktionsende aber nicht.
   Ein gemeinsamer Minutentakt fuer alle Instanzen, auch fuer die, die der
   Warenkorb-Drawer bei jedem Update neu einsetzt.
   ------------------------------------------------------------ */
(() => {
  'use strict';
  if (customElements.get('versandaktion-wechsel')) return;

  const instanzen = new Set();
  let takt = null;

  class VersandaktionWechsel extends HTMLElement {
    connectedCallback() {
      instanzen.add(this);
      this.pruefen();
      takt ??= setInterval(() => instanzen.forEach((el) => el.pruefen()), 60000);
    }

    disconnectedCallback() {
      instanzen.delete(this);
    }

    pruefen() {
      const jetzt = Date.now() / 1000;
      const aktiv = (this.dataset.fenster || '').split(',').some((f) => {
        const [beginn, ende] = f.split('-').map(Number);
        return beginn <= jetzt && jetzt < ende;
      });
      this.querySelectorAll(':scope > [data-zustand]').forEach((el) => {
        el.hidden = (el.dataset.zustand === 'an') !== aktiv;
      });
    }
  }

  customElements.define('versandaktion-wechsel', VersandaktionWechsel);
})();

/* Ton-Schalter der Video-Section: Autoplay laeuft stumm, der Schalter hebt das auf. */
document.addEventListener('click', (event) => {
  const toggle = event.target.closest('[data-video-sound-toggle]');
  if (!toggle) return;

  const video = toggle.closest('.content-box')?.querySelector('video');
  if (!video) return;

  video.muted = !video.muted;

  toggle.setAttribute('aria-pressed', String(!video.muted));
});

/* Play-Button in "Text mit Medien": das stumme Autoplay-Video startet von vorn mit Ton,
   danach uebernimmt die normale Video-Steuerung des Browsers (Pause, Ton, Zeitleiste,
   Vollbild). Freigeschaltet wird sie in custom.css ueber data-video-started. */
document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-video-play-sound]');
  if (!button) return;

  const box = button.closest('.content-box');
  const video = box?.querySelector('video');
  if (!video) return;

  box.setAttribute('data-video-started', '');
  video.currentTime = 0;
  video.muted = false;
  video.loop = false;
  video.controls = true;
  video.play().catch(() => {});
  button.hidden = true;
});

/* Verweigert der Browser das Autoplay (iOS-Energiesparmodus), setzt theme.js
   "suspended" und schaltet die nativen Controls ein. iOS ignoriert das Ausblenden
   per ::-webkit-media-controls und zeigt seinen Play-Button; Hintergrund-Loops
   bleiben deshalb ohne Controls und zeigen ihr Poster. */
new MutationObserver((mutations) => {
  mutations.forEach(({ target: video, oldValue }) => {
    if (oldValue !== null || !video.controls) return;
    if (!video.parentElement?.matches('video-media[autoplay][suspended]')) return;
    if (video.closest('[data-video-modal], [data-video-started]')) return;
    video.controls = false;
  });
}).observe(document.documentElement, { subtree: true, attributeFilter: ['controls'], attributeOldValue: true });

/* Videos nicht per Rechtsklick speichern; den Download-Button blendet
   controlslist="nodownload" in snippets/media.liquid aus. */
document.addEventListener('contextmenu', (event) => {
  if (event.target.closest('video, video-media')) event.preventDefault();
});
